import { memo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PhoneIcon, EmailIcon } from './icons';
import { useToast } from './ui';
import type { FooterTranslations } from '../types';

// WhatsApp Icon
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// Location Icon
const LocationIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

interface FooterProps {
  t: FooterTranslations;
  localizedPath: (path: string) => string;
}

const Footer = memo<FooterProps>(function Footer({ t, localizedPath }) {
  const logoUrl = '/rp_logo.webp';
  const currentYear = new Date().getFullYear();
  const { showToast } = useToast();
  const location = useLocation();
  
  // Check if we're on the home page
  const isHome = location.pathname === '/' || location.pathname === '/he' || location.pathname === '/he/';
  
  // Social links - update these with actual URLs
  // Note: Using # as placeholder - when actual URLs are added, remove role="button"
  const socialLinks = {
    linkedin: 'https://www.linkedin.com/in/offengenden/',
    facebook: '#', // User will add Facebook URL
    instagram: '#', // User will add Instagram URL
  };
  
  // Check if links are placeholders
  const isPlaceholder = (url: string) => url === '#';
  
  // Handle placeholder link click
  const handlePlaceholderClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    showToast(t.socialLinkComingSoon, 3000);
  }, [showToast, t.socialLinkComingSoon]);
  
  // WhatsApp number (Israel format)
  const whatsappNumber = '972747602660';
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  return (
    <footer className="bg-ink text-slate-400" role="contentinfo">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-brand-600 to-brand py-12">
        <div className="w-full px-6 sm:px-12 lg:px-16 xl:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">{t.ctaTitle}</h3>
              <p className="text-white/80">{t.desc}</p>
            </div>
            <div className="flex gap-4">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-xl hover:bg-green-600 transition-all"
                aria-label={`${t.whatsappChat} (opens in new window)`}
              >
                <WhatsAppIcon className="w-5 h-5" />
                {t.whatsappChat}
              </a>
              {isHome ? (
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-brand font-bold rounded-xl hover:bg-paper-muted transition-all"
                >
                  {t.ctaButton}
                </a>
              ) : (
                <Link
                  to={localizedPath('contact')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-brand font-bold rounded-xl hover:bg-paper-muted transition-all"
                >
                  {t.ctaButton}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="pt-16 pb-10">
        <div className="w-full px-6 sm:px-12 lg:px-16 xl:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img
                  src={logoUrl}
                  alt="Ronnie Projects"
                  className="h-8 w-auto brightness-0 invert"
                  width={488}
                  height={476}
                  loading="lazy"
                />
              </div>
              <p className="mb-6 leading-relaxed text-slate-500">{t.desc}</p>
              
              {/* Location */}
              <div className="flex items-center gap-2 mb-6 text-slate-500">
                <LocationIcon className="w-5 h-5 text-brand-400" />
                <span>{t.location}</span>
              </div>
              
              {/* Social Links */}
              <div className="flex gap-3">
                {/* LinkedIn */}
                <a
                  href={socialLinks.linkedin}
                  target={isPlaceholder(socialLinks.linkedin) ? undefined : "_blank"}
                  rel={isPlaceholder(socialLinks.linkedin) ? undefined : "noopener noreferrer"}
                  role={isPlaceholder(socialLinks.linkedin) ? "button" : undefined}
                  aria-label={isPlaceholder(socialLinks.linkedin) ? `LinkedIn (${t.comingSoon})` : t.followLinkedin}
                  className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-brand hover:text-white transition-all"
                  onClick={isPlaceholder(socialLinks.linkedin) ? (e) => e.preventDefault() : undefined}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
                
                {/* Facebook */}
                <a
                  href={socialLinks.facebook}
                  target={isPlaceholder(socialLinks.facebook) ? undefined : "_blank"}
                  rel={isPlaceholder(socialLinks.facebook) ? undefined : "noopener noreferrer"}
                  role={isPlaceholder(socialLinks.facebook) ? "button" : undefined}
                  aria-label={isPlaceholder(socialLinks.facebook) ? `Facebook (${t.comingSoon})` : t.followFacebook}
                  className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-brand hover:text-white transition-all"
                  onClick={isPlaceholder(socialLinks.facebook) ? handlePlaceholderClick : undefined}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                
                {/* Instagram */}
                <a
                  href={socialLinks.instagram}
                  target={isPlaceholder(socialLinks.instagram) ? undefined : "_blank"}
                  rel={isPlaceholder(socialLinks.instagram) ? undefined : "noopener noreferrer"}
                  role={isPlaceholder(socialLinks.instagram) ? "button" : undefined}
                  aria-label={isPlaceholder(socialLinks.instagram) ? `Instagram (${t.comingSoon})` : t.followInstagram}
                  className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-brand hover:text-white transition-all"
                  onClick={isPlaceholder(socialLinks.instagram) ? handlePlaceholderClick : undefined}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                
                {/* WhatsApp */}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${t.whatsappChat} (opens in new window)`}
                  className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-green-500 hover:text-white transition-all"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Services */}
            <nav aria-label={t.servicesTitle}>
              <h4 className="text-white font-bold text-sm mb-6 uppercase tracking-wider">
                {t.servicesTitle}
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to={localizedPath('ai-agents')}
                    className="hover:text-white transition-colors text-sm"
                  >
                    {t.aiAgents}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localizedPath('custom-development')}
                    className="hover:text-white transition-colors text-sm"
                  >
                    {t.customSoftware}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localizedPath('integration-services')}
                    className="hover:text-white transition-colors text-sm"
                  >
                    {t.systemIntegration}
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Company */}
            <nav aria-label={t.companyTitle}>
              <h4 className="text-white font-bold text-sm mb-6 uppercase tracking-wider">
                {t.companyTitle}
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to={localizedPath('about')} className="hover:text-white transition-colors text-sm">
                    {t.about}
                  </Link>
                </li>
                <li>
                  <Link to={localizedPath('case-studies')} className="hover:text-white transition-colors text-sm">
                    {t.caseStudies}
                  </Link>
                </li>
                <li>
                  <Link to={localizedPath('articles')} className="hover:text-white transition-colors text-sm">
                    {t.articles}
                  </Link>
                </li>
                <li>
                  <Link to={localizedPath('resources')} className="hover:text-white transition-colors text-sm">
                    {t.resources}
                  </Link>
                </li>
                <li>
                  {isHome ? (
                    <a
                      href="#contact"
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById('contact');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="hover:text-white transition-colors text-sm"
                    >
                      {t.contact}
                    </a>
                  ) : (
                    <Link to={localizedPath('contact')} className="hover:text-white transition-colors text-sm">
                      {t.contact}
                    </Link>
                  )}
                </li>
              </ul>
            </nav>

            {/* Contact */}
            <div>
              <h4 className="text-white font-bold text-sm mb-6 uppercase tracking-wider">
                {t.contactTitle}
              </h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a
                    href="tel:+972747602660"
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <PhoneIcon className="w-4 h-4 text-brand-400" />
                    <span dir="ltr">+972 74 760 2660</span>
                  </a>
                </li>
                <li>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-white transition-colors"
                    aria-label={`${t.whatsappChat} (opens in new window)`}
                  >
                    <WhatsAppIcon className="w-4 h-4 text-green-400" />
                    <span>{t.whatsappChat}</span>
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@ronnieprojects.com"
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <EmailIcon className="w-4 h-4 text-brand-400" />
                    <span>info@ronnieprojects.com</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
            <p>
              © {currentYear} <span lang="en">Ronnie Projects</span>. {t.copyright}
            </p>
            <div className="flex gap-6">
              <Link to={localizedPath('privacy')} className="hover:text-white transition-colors">
                {t.privacy}
              </Link>
              <Link to={localizedPath('terms')} className="hover:text-white transition-colors">
                {t.terms}
              </Link>
              <Link to={localizedPath('accessibility')} className="hover:text-white transition-colors">
                {t.accessibility}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
